/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */
define([
	'../../dataSources/utils/DatasourceUtil',
	'underscore',
	'../../widgets/livewidget/nls/StringResources',
	'../../util/ContentUtil',
	'../../lib/@waca/dashboard-common/dist/utils/ContentUtil'
], function(DatasourceUtil, _, StringResources, ContentUtil, ContentUtilCommon) {

	var paramProperties = ['name', 'modelFilterItem', 'type', 'capabilities'];

	let REPORT = 'report';

	class DrillThroughMappingManager {

		/**
		 * @param {object} dashboardApi - Dashboard API
		 * @param {object} drillService - drill service
		 */
		constructor (dashboardApi, drillService) {
			this.dashboard = dashboardApi;
			this.drillService = drillService;
		}

		/**
		 * @param storeId -  The cm store id of the target report
		 */
		getParameters (storeId) {
			return this.dashboard.getGlassSvc('.DrillInfoService').then(drillInfoService => {
				var attrs = {
					type: REPORT,
					storeId: storeId
				};

				return drillInfoService.getParameters(attrs)
					.then(response => {
						var parameters = response.parameters || [];
						var result = [];
						_.each(parameters, function (parameter) {
							result.push(_.pick(parameter, paramProperties));
						});
						return result;
					}).catch((e) => {
						this.dashboard.getGlassCoreSvc('.Logger').error('Could not get prompt information from the selected report.', e);
						this.dashboard.showErrorMessage(StringResources.get('drillThroughDefinitionErrorQueryingReport'), StringResources.get('drillThroughDefinitionErrorDlgTitle'));
					});
			});
		}

		/**
		 * function to map drill target parameters to current dashboard metadata, also returns the metadata
		 * columns within the current context, i.e. same data asset and scope (tab) as the definitionContent.
		 * @param {string} storeId - The cm store id of the target report
		 * @param {object} definitionContent - the content that the drill through definition is based on.
		 * @param {string} mappings - The existing mappings from a drill definition entry.
		 */
		doMapping (storeId, definitionContent, mappings) {
			return this.getParameters(storeId).then(parameters => {
				if (!parameters.length) {
					return Promise.resolve();
				}
				const datasourceAPI =  definitionContent.getFeature('Visualization').getDataSource();
				return datasourceAPI.getModule().then(module => this._mapToMetadata(parameters, definitionContent, mappings, module));
			});
		}

		/**
		 * @param {object} parameters - parameters in the target
		 * @param {object} definitionContent - the content that the drill through definition is based on.
		 * @param {string} mappings - The existing mappings from a drill definition entry.
		 * @param {object} module - module api
		 */
		_mapToMetadata (parameters, definitionContent, mappings, module) {
			const metadataColumnIds = this.getMetadataIds(definitionContent, module);
			let mapToColumnIds = []; // columns exist in drill entry mappings, but not in the current content context
			parameters.forEach(function(parameter) {
				// If saved mappings is passed in, propagate the mapTo to the parameter and do not remap from the current
				// content context
				if (mappings) {
					let mappedParameter = _.find(mappings, (mapping) => {
						return mapping.modelFilterItem === parameter.modelFilterItem && mapping.name === parameter.name;
					});
					if (mappedParameter && mappedParameter.mapTo) {
						parameter.mapTo = mappedParameter.mapTo;
						if (metadataColumnIds.indexOf(mappedParameter.mapTo) === -1) {
							mapToColumnIds.push(mappedParameter.mapTo);
						}
					}
				} else {
					// New definition: map the parameter to the current content context
					parameter.mapTo = _.find(metadataColumnIds, (columnId) => {
						return parameter.modelFilterItem === columnId;
					});
				}
			});

			return {
				parameterMappings: parameters,
				columnLabels: this._getColumnLabels(definitionContent, metadataColumnIds.concat(mapToColumnIds))
			};
		}

		_getColumnLabels (definitionContent, metadataColumnIds) {
			// Collect the column names based on the metadataIds
			var columnLabels = [];
			const visualization = definitionContent.getFeature('Visualization');
			const dataSource = visualization.getDataSource();
			metadataColumnIds.forEach(columnId => {
				var metadataColumn = !DatasourceUtil.isMultiMeasuresSeriesOrValue(columnId) ? dataSource.getMetadataColumn(columnId) : null;
				if (metadataColumn) {
					columnLabels.push({
						columnId: columnId,
						label: metadataColumn.getLabel()
					});
				}
			});
			return _.sortBy(columnLabels, 'label');
		}

		/**
		 * @returns data item ids of the corresponding context, i.e. same data asset and scope
		 * from dashboard model
		 */
		getMetadataIds (definitionContent, module) {
			const metadataIds = [];
			const visualization = definitionContent.getFeature('Visualization');
			const dataSource = visualization.getDataSource();
			// Collect metadata from content
			const eventGroups = this.dashboard.getFeature('EventGroups');
			if (eventGroups) {
				const groupId = eventGroups.getGroupId(definitionContent.getId());
				const contentList = eventGroups.getContentIdList(groupId);
				contentList.forEach((contentId) => {
					metadataIds.push(...this._collectWidgetMetadata(contentId, dataSource.getId()));
				});
			}

			// Collect metadata from localFilters of the visualization
			this._collectLocalFilterMetadata(visualization, metadataIds);

			// Collect metadata from pageContext of the definitionContent scope
			this._collectPageContextMetadata(definitionContent, metadataIds, visualization, 'visualization', module);
			this._collectPageContextMetadata(definitionContent, metadataIds, visualization, 'filter', module);
			return _.uniq(metadataIds);
		}

		_collectWidgetMetadata (contentId, sourceId) {
			const content = this.dashboard.getCanvas().getContent(contentId);
			let metadataIds = [];
			const dataSource = content.getFeature('Visualization').getDataSource();
			if (!dataSource || dataSource.getId() !== sourceId) {
				return metadataIds;
			}

			const slots = content.getFeature('Visualization').getSlots();
			slots.getDataItemList().forEach(dataItem=>{
				const metadataColumn = dataItem.getMetadataColumn();
				if (metadataColumn) {
					metadataIds.push(metadataColumn.getId());
				}
			});
			return metadataIds;
		}

		_collectLocalFilterMetadata (visualization, metadataIds) {
			const localFilters = visualization.getLocalFilters().getFilterList();

			// Collect metadata from local filters.
			localFilters.forEach(function(filter) {
				metadataIds.push(filter.columnId);
			});
		}

		_collectPageContextMetadata (definitionContent, metadataIds, visualization, origin, module) {
			//@todo: Endor add support for multiple data sources
			const type = this.dashboard.getAppConfig('pageContainerType');
			const pageContent = ContentUtilCommon.getPageContent(definitionContent, type);
			const pageContext = ContentUtil.getPageContext(this.dashboard);
			const dataSource = visualization.getDataSource();
			var pageContextItems = pageContext.getNetPageContextItems({
				scope: pageContent && pageContent.getId(),
				sourceId: dataSource.getId(),
				origin: origin
			});
			if (!pageContextItems.length) {
				return;
			}


			if (origin === 'filter') {
				pageContextItems = DrillThroughMappingManager.getNetSynchronizedFilterPageContext(visualization, definitionContent, pageContextItems, module);
			}

			pageContextItems.forEach(contextItem => {
				metadataIds.push(contextItem.getItemId());
			});
		}

		/**
		* Returns an array of net synchronized filter page context
		*
		* @param {object} visualization - the content visualization
		* @param {object} ownerContent - the livewidget instance
		* @param {object} filterPageContextItems - the page context filter items
		* @param {object} module - module api
		*
		* @return {array} merged synchronized and filter net page context array
		*/
		static getNetSynchronizedFilterPageContext(visualization, ownerContent, filterPageContextItems, module) {
			const dataSource = visualization.getDataSource();
			if (dataSource.getType() === 'package') {
				return filterPageContextItems;
			}
			const mappedSlots = visualization.getSlots().getMappedSlotList();
			const tableRef = [];
			mappedSlots.forEach(mappedSlot => {
				const dataItems = mappedSlot.getDataItemList();
				dataItems.forEach(dataItem => {
					tableRef.push(dataItem.getMetadataColumn().getTableName());
				});
			});
			filterPageContextItems = filterPageContextItems.filter(pageContextItem => {
				let paggeContextTableRef = DatasourceUtil.getPageContextItemTableRef(dataSource, pageContextItem.getPageContextSpec());
				return DatasourceUtil.haveTableJoinsInSameDataSource(module, paggeContextTableRef, tableRef);
				//return  dataSource.hasJoinedTables(paggeContextTableRef, tableRef);
			});

			// TODO: last widget reference in JumpTo to be removed
			const widget = ownerContent.getFeature('WidgetAPI.deprecated').getVisApi().ownerWidget;
			return DatasourceUtil.mergeSynchronizePageContextFilter(filterPageContextItems, widget.getSynchDataFilterEntries());
		}
	}

	return DrillThroughMappingManager;

});
